import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SectionHeader from './SectionHeader';

const PRIMARY = 'rgb(5, 70, 37)';

const ShopAvatar = ({ shop, size = 68, active = false }) => {
  const [imgError, setImgError] = useState(false);
  const ringStyle = { width: size, height: size, borderColor: active ? PRIMARY : 'rgb(229,231,235)' };

  if (shop.image_url && !imgError) {
    return (
      <img
        src={shop.image_url}
        alt=""
        className="rounded-full object-cover flex-shrink-0 border"
        style={ringStyle}
        onError={() => setImgError(true)}
      />
    );
  }

  const initials = (shop.name || '?')
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('');

  return (
    <div
      className="rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-base select-none border"
      style={{ ...ringStyle, backgroundColor: PRIMARY }}
    >
      {initials}
    </div>
  );
};

const GroceryShopsCarousel = ({ shops = [], selectedShopId, onSelectShop }) => {
  const { t } = useTranslation();
  const trackRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      ro.disconnect();
    };
  }, [shops]);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 240, behavior: 'smooth' });
  };

  if (!shops.length) return null;

  return (
    <div className="relative mb-6">
      <SectionHeader
        title={t('home.grocery.shopsNearYou')}
        onScrollLeft={() => scroll(-1)}
        onScrollRight={() => scroll(1)}
        canScrollLeft={canScrollLeft}
        canScrollRight={canScrollRight}
      />

      <div className="relative">
        {/* Track */}
        <div
          ref={trackRef}
          className="flex gap-5 overflow-x-auto pb-1 scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {/* Carte "Tous" */}
          <button
            type="button"
            onClick={() => onSelectShop(null)}
            className="flex-shrink-0 flex flex-col items-center gap-2 cursor-pointer focus:outline-none group"
          >
            <div
              className="w-[68px] h-[68px] rounded-full border flex items-center justify-center transition-all"
              style={{
                borderColor: selectedShopId === null ? PRIMARY : 'rgb(229,231,235)',
                backgroundColor: selectedShopId === null ? 'rgba(5,70,37,0.08)' : '#f3f4f6',
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" className="w-7 h-7" style={{ color: PRIMARY }}>
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20M12 2a15.3 15.3 0 0 1 0 20M12 2a15.3 15.3 0 0 0 0 20" />
              </svg>
            </div>
            <span
              className="text-sm font-semibold whitespace-nowrap max-w-[4.5rem] truncate"
              style={{ color: selectedShopId === null ? PRIMARY : 'rgb(55,65,81)' }}
            >
              {t('home.allCategories')}
            </span>
          </button>

          {/* Cartes magasins */}
          {shops.map((shop) => {
            const isActive = selectedShopId === shop.id;
            return (
              <button
                key={shop.id}
                type="button"
                onClick={() => onSelectShop(isActive ? null : shop.id)}
                className="flex-shrink-0 flex flex-col items-center gap-2 cursor-pointer focus:outline-none"
              >
                <ShopAvatar shop={shop} size={68} active={isActive} />
                <span
                  className="text-sm font-semibold whitespace-nowrap max-w-[5rem] truncate"
                  style={{ color: isActive ? PRIMARY : 'rgb(55,65,81)' }}
                >
                  {shop.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Nom du magasin sélectionné */}
      {selectedShopId !== null && (() => {
        const shop = shops.find(s => s.id === selectedShopId);
        return shop ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Link
              to={`/grocery-shop/${shop.id}`}
              className="text-sm font-semibold transition hover:text-slate-900"
              style={{ color: PRIMARY }}
            >
              {shop.name}
            </Link>
            {shop.city && <span className="text-xs text-gray-400">{shop.city}</span>}
            {shop.description && (
              <span className="text-xs text-gray-400 truncate max-w-xs hidden sm:inline">· {shop.description}</span>
            )}
          </div>
        ) : null;
      })()}
    </div>
  );
};

export default GroceryShopsCarousel;
